import React from "react";
import { colors } from "@/ui";

/**
 * StatPill — the rounded header pill iOS uses for the streak badge
 * (client-native AppTopBar.tsx:74-85 + badge style :112-120).
 *
 * iOS metrics: paddingHorizontal 12, paddingVertical 6, borderRadius 16,
 * gap 4, xs/semibold text. Two themes mirror AppTopBar's badgeTheme:
 *  - onDark : bg --overlay-white-medium (rgba(255,255,255,0.20)), white text, no border
 *  - onLight: bg white, 1px --color-grey-lightest border, --color-text-primary text
 *
 * The words pill renders through the shared `@/ui` WordsBadge (same iOS
 * `badge` block); this atom covers the streak (and any future icon+value pill).
 */
export interface StatPillProps {
  /** Leading glyph (emoji or icon node). */
  icon: React.ReactNode;
  /** Primary value (e.g. day-streak number). */
  value: React.ReactNode;
  /** Optional trailing label (e.g. "words"). */
  label?: string;
  theme?: "onDark" | "onLight";
  onClick?: () => void;
  ariaLabel?: string;
}

const StatPill: React.FC<StatPillProps> = ({
  icon,
  value,
  label,
  theme = "onLight",
  onClick,
  ariaLabel,
}) => {
  const interactive = typeof onClick === "function";
  const isDark = theme === "onDark";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 6,
        paddingBottom: 6,
        borderRadius: 16,
        backgroundColor: isDark ? "var(--overlay-white-medium)" : colors.white,
        border: isDark ? "none" : "1px solid var(--color-grey-lightest)",
        color: isDark ? colors.white : "var(--color-text-primary)",
        fontSize: "var(--base-font-size-xs)",
        fontWeight: 600,
        lineHeight: 1,
        cursor: interactive ? "pointer" : "default",
        boxSizing: "border-box",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <span
        aria-hidden
        style={{
          fontSize: 16,
          lineHeight: 1,
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        {icon}
      </span>
      <span>{value}</span>
      {label && <span>{label}</span>}
    </button>
  );
};

export default StatPill;
